import { useMemo } from 'react';
import { useRouterState, useSearch } from '@tanstack/react-router';

import { PRODUCT_STEPS, getNextProductStepUrl } from '@/lib/products';
import type { TanstackUrl } from '@/lib/stepper';

import { useEffectiveCategoryLabels } from './use-effective-category-labels';

const PRODUCT_SELECTION_PATHNAMES = new Set<string>(
  PRODUCT_STEPS.filter((step) => step.url !== '/products/summary').map((step) => step.url as string)
);

function nextStepSearchOverride(pathname: string, search: unknown): TanstackUrl | undefined {
  if (!PRODUCT_SELECTION_PATHNAMES.has(pathname)) return undefined;
  if (!search || typeof search !== 'object' || !('nextStep' in search)) return undefined;
  const raw = (search as { nextStep?: unknown }).nextStep;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  return raw as TanstackUrl;
}

// Get the next step from the search params or the session store
// Available steps are defined in PRODUCT_STEPS and filtered by the selected categories from
// earlier in the intake flow
export function useProductSelectionStep(): {
  nextStep: TanstackUrl;
} {
  const effectiveCategoryLabels = useEffectiveCategoryLabels();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const searchOverride = useSearch({
    strict: false,
    shouldThrow: false,
    select: (search) => nextStepSearchOverride(pathname, search),
  });

  const sessionNext = getNextProductStepUrl(effectiveCategoryLabels);

  return useMemo(
    () => ({
      nextStep: (searchOverride ?? sessionNext) as TanstackUrl,
    }),
    [searchOverride, sessionNext]
  );
}
